/*
  Team store items in 3D: hoodie, T-shirt, tracksuit jacket, tracksuit pants, socks and beanie,
  each with the club's crest and the Vortex mark. Built from the same jersey as the kit designer
  (kit-model.js) plus simple garment shapes, rendered once into each card and turned while hovered.

  Markup: <canvas data-3d-item data-type="hoodie" data-colour="#111111" data-trim="#B3261E"
  data-crest="...svg"> inside a [data-store-3d] box that carries the script URLs.
*/
const box = document.querySelector('[data-store-3d]');
if (box) start();

async function start() {
  const items = Array.from(document.querySelectorAll('[data-3d-item]'));
  if (!items.length) return;
  let T; let P; let M;
  try {
    [T, P, M] = await Promise.all([import(box.dataset.three), import(box.dataset.paint), import(box.dataset.kitModel)]);
  } catch (e) { return; } // the drawings stay
  const { gridGeometry } = M;

  const SIZE = 640;
  let renderer;
  try {
    renderer = new T.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  } catch (e) { return; }
  renderer.setSize(SIZE, SIZE, false);
  renderer.outputColorSpace = T.SRGBColorSpace;
  const scene = new T.Scene();
  scene.add(new T.HemisphereLight(0xffffff, 0xb8b0a2, 2.0));
  const key = new T.DirectionalLight(0xffffff, 1.7);
  key.position.set(1.4, 2.2, 3);
  scene.add(key);
  const rim = new T.DirectionalLight(0xffffff, 0.7);
  rim.position.set(-2, 1, -2);
  scene.add(rim);
  const camera = new T.PerspectiveCamera(24, 1, 0.05, 30);

  /* ---------- images for the logos ---------- */
  const load = (src) => new Promise((r) => { if (!src) { r(null); return; } const i = new Image(); i.onload = () => r(i); i.onerror = () => r(null); i.src = src; });
  const svg = document.querySelector('[data-store-logo]');
  const markFor = new Map();
  const mark = async (colour) => {
    if (!markFor.has(colour)) markFor.set(colour, await P.makeLogo(svg && svg.content.querySelector('svg'), colour));
    return markFor.get(colour);
  };

  /* ---------- small helpers ---------- */
  const mat = (colour, rough = 0.78) => new T.MeshStandardMaterial({ color: colour, roughness: rough, side: T.DoubleSide });
  const texMat = (canvas) => {
    const t = new T.CanvasTexture(canvas);
    t.colorSpace = T.SRGBColorSpace;
    t.wrapS = 1000;
    t.anisotropy = 4;
    return new T.MeshStandardMaterial({ map: t, roughness: 0.8, side: T.DoubleSide });
  };
  const canvas = (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };
  // a tube along a path of [x,y,z] points, radius r(t) (or [rx, rz]), u around, v along
  const tube = (path, r, opts = {}) => {
    const pts = path;
    const at = (t) => {
      const f = t * (pts.length - 1);
      const i = Math.min(pts.length - 2, Math.floor(f));
      const k = f - i;
      return pts[i].map((v, j) => v + (pts[i + 1][j] - v) * k);
    };
    return gridGeometry(T, 40, opts.rows || 40, (u, t) => {
      const c = at(t);
      const n = at(Math.min(1, t + 0.01));
      const pv = at(Math.max(0, t - 0.01));
      const dir = [n[0] - pv[0], n[1] - pv[1], n[2] - pv[2]];
      const l = Math.hypot(...dir) || 1;
      dir.forEach((v, j) => { dir[j] = v / l; });
      // a frame square to the path: side is x, the other is cross(dir, x)
      let side = [1, 0, 0];
      const d = side[0] * dir[0];
      side = [side[0] - d * dir[0], -d * dir[1], -d * dir[2]];
      const sl = Math.hypot(...side) || 1;
      side = side.map((v) => v / sl);
      const up = [dir[1] * side[2] - dir[2] * side[1], dir[2] * side[0] - dir[0] * side[2], dir[0] * side[1] - dir[1] * side[0]];
      const rr = typeof r === 'function' ? r(t) : r;
      const [rx, rz] = Array.isArray(rr) ? rr : [rr, rr];
      const th = u * Math.PI * 2;
      return { p: [c[0] + side[0] * Math.sin(th) * rx + up[0] * Math.cos(th) * rz, c[1] + side[1] * Math.sin(th) * rx + up[1] * Math.cos(th) * rz, c[2] + side[2] * Math.sin(th) * rx + up[2] * Math.cos(th) * rz], u, v: 1 - t };
    }, { seamCol: true, inside: (p) => p.map((v, j) => at(0.5)[j]) });
  };

  /* ---------- the garments ---------- */
  const design = (o) => P.clean({
    cut: o.cut || 'setin',
    panels: {
      front: { c: o.colour, p: 'none' }, back: { c: o.colour, p: 'none' },
      sleeves: { c: o.colour, p: 'none' }, sides: { c: o.sides || o.colour, p: 'none' },
    },
    collar: o.collar || 'crew', collarHex: o.collarHex || o.trim, markHex: '', number: '', names: false, sponsor: '',
    shorts: { c: o.colour, p: 'none' }, socks: { c: o.colour, p: 'none' },
  });
  const LONG = { hole: P.SLEEVE.hole, end: [0.33, 0.22, -0.03], rEnd: [0.046, 0.05], cuff: 0.05 };

  const shirt = (o, extra) => {
    const j = M.createJersey(T, P, { scale: 0.5, sleeve: extra.long ? LONG : null });
    j.set(design({ ...o, ...extra }));
    j.group.position.y = -0.55;
    return j.group;
  };

  const builders = {
    tee: (o) => shirt(o, { collar: 'crew', collarHex: o.trim }),
    jacket: (o) => {
      const g = new T.Group();
      g.add(shirt(o, { long: true, collar: 'hybrid', collarHex: o.colour, sides: o.trim }));
      // the zip, down the front
      const zip = new T.Mesh(gridGeometry(T, 2, 40, (u, t) => {
        const y = 0.2 + t * 0.62;
        const tt = P.torso(y);
        return { p: [(u - 0.5) * 0.008, y - 0.55, tt.zc + tt.bf + 0.012], u, v: t };
      }), mat('#1A1A1A', 0.4));
      g.add(zip);
      return g;
    },
    hoodie: (o) => {
      const g = new T.Group();
      g.add(shirt(o, { long: true, collar: 'crew', collarHex: o.colour }));
      // the hood, down, resting round the neck and over the upper back
      const hood = gridGeometry(T, 60, 16, (u, t) => {
        const phi = -Math.PI + u * Math.PI * 2;
        const back = (1 - Math.cos(phi)) / 2; // 0 front, 1 back
        const y0 = P.neckY(phi, 'crew');
        const tt = P.torso(y0);
        const r = 0.03 + 0.06 * back ** 1.5;
        const a = t * Math.PI * 1.6 - Math.PI * 0.3;
        const base = P.torsoPoint(phi, y0, 0.02);
        const outx = Math.sin(phi);
        const outz = Math.cos(phi);
        return { p: [base[0] + outx * r * (1 + Math.cos(a)) * 0.6, y0 - 0.55 + r * Math.sin(a) * 0.9 - back * 0.03, base[2] + outz * r * (1 + Math.cos(a)) * 0.6 - back * 0.01 + 0 * tt.zc], u, v: t };
      }, { seamCol: true, inside: (p) => [0, p[1], -0.035] });
      g.add(new T.Mesh(hood, mat(o.colour)));
      // lining shows at the opening
      // pocket across the front
      const pocket = gridGeometry(T, 20, 6, (u, t) => {
        const y = 0.27 + t * 0.12;
        const tt = P.torso(y);
        const x = (u - 0.5) * 0.24 * (1 - t * 0.25);
        const z = tt.zc + (tt.bf + 0.012) * Math.sqrt(Math.max(0, 1 - (x / (tt.a + 0.01)) ** 2)) + 0.004;
        return { p: [x, y - 0.55, z], u, v: t };
      }, { inside: (p) => [p[0], p[1], p[2] - 1] });
      g.add(new T.Mesh(pocket, mat(P.shade(o.colour, 0.85))));
      // drawstrings
      [-0.025, 0.025].forEach((x) => {
        const s = new T.Mesh(new T.CylinderGeometry(0.003, 0.003, 0.11, 6), mat(o.trim));
        const tt = P.torso(0.79);
        s.position.set(x, 0.79 - 0.55 - 0.05, tt.zc + tt.bf + 0.02);
        g.add(s);
      });
      return g;
    },
    pants: (o, imgs) => {
      const g = new T.Group();
      const c = canvas(1024, 1024);
      const ctx = c.getContext('2d');
      ctx.fillStyle = o.colour; ctx.fillRect(0, 0, 1024, 1024);
      ctx.fillStyle = o.trim;
      [0.25, 0.75].forEach((u) => ctx.fillRect(u * 1024 - 10, 0, 20, 1024)); // side stripes
      ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fillRect(0, 0, 1024, 60); // waistband
      const m = texMat(c);
      const legTex = (side) => {
        const lc = canvas(512, 1024);
        const lx = lc.getContext('2d');
        lx.fillStyle = o.colour; lx.fillRect(0, 0, 512, 1024);
        lx.fillStyle = o.trim; lx.fillRect(side > 0 ? 116 : 376, 0, 20, 1024); // stripe down the outside
        lx.fillStyle = 'rgba(0,0,0,0.25)'; lx.fillRect(0, 990, 512, 34); // cuff
        // crest high on the wearer's left thigh, the mark on the right
        if (side > 0 && imgs.crest) P.drawFit(lx, imgs.crest, 256, 150, 110, 120);
        if (side < 0 && imgs.mark) P.drawFit(lx, imgs.mark, 256, 150, 70, 70);
        return texMat(lc);
      };
      // hips: waist down past the crotch
      g.add(new T.Mesh(tube([[0, 0.2, -0.02], [0, 0.0, -0.02]], (t) => [0.19 + 0.02 * t, 0.12], { rows: 8 }), m));
      [1, -1].forEach((side) => {
        g.add(new T.Mesh(tube([[side * 0.1, 0.06, -0.02], [side * 0.11, -0.4, -0.03], [side * 0.12, -0.82, -0.04]], (t) => [0.095 - 0.04 * t, 0.1 - 0.04 * t]), legTex(side)));
      });
      return g;
    },
    socks: (o, imgs) => {
      const g = new T.Group();
      [1, -1].forEach((side) => {
        const c = canvas(512, 1024);
        const ctx = c.getContext('2d');
        ctx.fillStyle = o.colour; ctx.fillRect(0, 0, 512, 1024);
        ctx.fillStyle = 'rgba(0,0,0,0.18)'; ctx.fillRect(0, 0, 512, 50);
        ctx.fillStyle = o.trim; ctx.fillRect(0, 80, 512, 46); ctx.fillRect(0, 150, 512, 46);
        // crest on the shin (front), the mark on the calf (back)
        if (imgs.crest) P.drawFit(ctx, imgs.crest, 256, 330, 120, 130);
        if (imgs.mark) { P.drawFit(ctx, imgs.mark, 0, 330, 80, 80); P.drawFit(ctx, imgs.mark, 512, 330, 80, 80); }
        const path = [[0, 0.3, 0], [0, 0.0, 0], [0, -0.28, 0], [0, -0.36, 0.03], [0, -0.39, 0.12], [0, -0.39, 0.2]];
        const sock = new T.Mesh(tube(path, (t) => [0.05 - 0.008 * t, 0.05 - 0.01 * t + (t > 0.75 ? -0.015 : 0)]), texMat(c));
        sock.position.x = side * 0.09;
        sock.rotation.y = side * 0.12;
        g.add(sock);
      });
      return g;
    },
    beanie: (o, imgs) => {
      const g = new T.Group();
      // dome
      const dome = gridGeometry(T, 60, 30, (u, t) => {
        const a = t * Math.PI / 2;
        const th = u * Math.PI * 2;
        const r = 0.12 * Math.cos(a) * (1 + 0.04 * Math.sin(a * 6));
        return { p: [Math.sin(th) * r, 0.04 + 0.13 * Math.sin(a), Math.cos(th) * r], u, v: t };
      }, { seamCol: true, inside: () => [0, 0.05, 0] });
      const knit = canvas(512, 512);
      const kx = knit.getContext('2d');
      kx.fillStyle = o.colour; kx.fillRect(0, 0, 512, 512);
      kx.strokeStyle = 'rgba(0,0,0,0.25)'; kx.lineWidth = 3;
      for (let x = 0; x < 512; x += 12) { kx.beginPath(); kx.moveTo(x, 0); kx.lineTo(x, 512); kx.stroke(); }
      g.add(new T.Mesh(dome, texMat(knit)));
      // cuff with the crest
      const c = canvas(1024, 256);
      const ctx = c.getContext('2d');
      ctx.fillStyle = P.shade(o.colour, 0.9); ctx.fillRect(0, 0, 1024, 256);
      ctx.strokeStyle = 'rgba(0,0,0,0.25)'; ctx.lineWidth = 4;
      for (let x = 0; x < 1024; x += 14) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 256); ctx.stroke(); }
      if (imgs.crest) P.drawFit(ctx, imgs.crest, 512, 128, 150, 170);
      if (imgs.mark) P.drawFit(ctx, imgs.mark, 0, 128, 70, 70);
      g.add(new T.Mesh(tube([[0, 0.08, 0], [0, -0.01, 0]], 0.128, { rows: 4 }), texMat(c)));
      // pom
      const pom = gridGeometry(T, 24, 16, (u, t) => {
        const a = (t - 0.5) * Math.PI;
        const th = u * Math.PI * 2;
        const r = 0.045 * (1 + 0.08 * Math.sin(th * 9) * Math.cos(a * 7));
        return { p: [Math.sin(th) * Math.cos(a) * r, 0.17 + 0.04 + Math.sin(a) * r, Math.cos(th) * Math.cos(a) * r], u, v: t };
      }, { seamCol: true, inside: () => [0, 0.21, 0] });
      g.add(new T.Mesh(pom, mat(o.trim, 0.95)));
      return g;
    },
  };

  /* ---------- render each card ---------- */
  const built = [];
  const bundleBox = { tee: [0.05, 0.05, 0.5, 0.5], jacket: [0.4, 0.0, 0.55, 0.55], hoodie: [0.4, 0.0, 0.55, 0.55], pants: [0.62, 0.4, 0.36, 0.58], socks: [0.06, 0.55, 0.32, 0.42], beanie: [0.35, 0.6, 0.3, 0.3] };
  for (const el of items) {
    const type = el.dataset.type;
    if (type === 'bundle') {
      // several pieces laid out in one card, like a flat lay
      const list = (el.dataset.items || '').split(',').filter((t) => builders[t]);
      const parts = [];
      for (const t of list) {
        const o = { colour: el.dataset.colour || '#111111', trim: el.dataset.trim || '#B3261E' };
        const crest = await load(el.dataset.crest);
        const markImg = await mark(P.textOn(o.colour, '#FFFFFF'));
        const saved = { ...P.res };
        P.res.crest = crest; P.res.logo = markImg; P.res.sponsors = {};
        const obj = builders[t](o, { crest, mark: markImg });
        Object.assign(P.res, saved);
        const bounds = new T.Box3().setFromObject(obj);
        const c = new T.Vector3(); bounds.getCenter(c);
        const sz = new T.Vector3(); bounds.getSize(sz);
        obj.position.sub(c);
        const holder = new T.Group(); holder.add(obj);
        const dist = (Math.max(sz.x, sz.y, sz.z) * 0.62) / Math.tan(((camera.fov * Math.PI) / 180) / 2);
        parts.push({ holder, dist, yaw: t === 'socks' ? -0.6 : 0.25, box: bundleBox[t] || [0, 0, 0.5, 0.5] });
      }
      built.push({ el, parts, spin: false, yaw: 0 });
      continue;
    }
    if (!builders[type]) continue;
    const o = { colour: el.dataset.colour || '#111111', trim: el.dataset.trim || '#B3261E' };
    const crest = await load(el.dataset.crest);
    const markImg = await mark(P.textOn(o.colour, '#FFFFFF'));
    // the jersey painter reads its logo images from P.res
    const saved = { ...P.res };
    P.res.crest = crest;
    P.res.logo = markImg;
    P.res.sponsors = {};
    const obj = builders[type](o, { crest, mark: markImg });
    Object.assign(P.res, saved);
    // frame it
    const bounds = new T.Box3().setFromObject(obj);
    const centre = new T.Vector3();
    bounds.getCenter(centre);
    const size = new T.Vector3();
    bounds.getSize(size);
    obj.position.sub(centre);
    const holder = new T.Group();
    holder.add(obj);
    const dist = (Math.max(size.x, size.y, size.z) * 0.62) / Math.tan(((camera.fov * Math.PI) / 180) / 2);
    built.push({ el, holder, dist, yaw: type === 'socks' ? -0.6 : 0.35, spin: false });
  }

  const shoot = (holder, dist, yaw) => {
    scene.add(holder);
    holder.rotation.y = yaw;
    camera.position.set(0, dist * 0.12, dist);
    camera.lookAt(0, 0, 0);
    renderer.render(scene, camera);
    scene.remove(holder);
  };
  const draw = (b) => {
    if (b.parts) {
      const ctx = b.el.getContext('2d');
      ctx.clearRect(0, 0, b.el.width, b.el.height);
      b.parts.forEach((p) => {
        shoot(p.holder, p.dist, p.yaw + b.yaw);
        const [x, y, w, h] = p.box;
        ctx.drawImage(renderer.domElement, x * b.el.width, y * b.el.height, w * b.el.width, h * b.el.height);
      });
      return;
    }
    scene.add(b.holder);
    b.holder.rotation.y = b.yaw;
    camera.position.set(0, b.dist * 0.12, b.dist);
    camera.lookAt(0, 0, 0);
    renderer.render(scene, camera);
    scene.remove(b.holder);
    const ctx = b.el.getContext('2d');
    ctx.clearRect(0, 0, b.el.width, b.el.height);
    ctx.drawImage(renderer.domElement, 0, 0, b.el.width, b.el.height);
  };
  built.forEach((b) => {
    b.el.width = b.parts ? SIZE * 1.4 : SIZE; b.el.height = SIZE;
    draw(b);
    b.el.classList.add('is-ready');
    // turn while hovered or touched
    const card = b.el.closest('[data-3d-card]') || b.el;
    let raf = 0;
    let last = 0;
    const loop = (t) => {
      if (!b.spin) return;
      const dt = last ? Math.min(0.05, (t - last) / 1000) : 0;
      last = t;
      b.yaw += dt * (b.parts ? 0.8 : 1.4);
      draw(b);
      raf = requestAnimationFrame(loop);
    };
    card.addEventListener('pointerenter', () => { b.spin = true; last = 0; cancelAnimationFrame(raf); raf = requestAnimationFrame(loop); });
    card.addEventListener('pointerleave', () => { b.spin = false; });
  });
}
